import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Text } from 'foundations-components/components/Text';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIPanel from 'UIComponents/panel/UIPanel';
import UIPanelBody from 'UIComponents/panel/UIPanelBody';
import UIPanelFooter from 'UIComponents/panel/UIPanelFooter';
import UIPanelHeader from 'UIComponents/panel/UIPanelHeader';
import UIPanelSection from 'UIComponents/panel/UIPanelSection';
import UIButton from 'UIComponents/button/UIButton';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Box } from 'foundations-components/transitional/components/Box';
import { Link } from 'foundations-components/transitional/components/Link';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
// @ts-expect-error this file has no types
import CurrencySelect from 'ui-addon-i18n/components/CurrencySelect';
//@ts-expect-error untyped
import { currencyStyles } from 'I18n/internal/utils/CurrencyLabelFormatters';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import Links from '../../constants/Links';
import { getAvailableCurrencies, getCompanyCurrency } from '../../selectors/multiCurrencySelectors';
import { getIsDealStageView } from '../../selectors/forecastSelectors';
import useFetchPreferredCurrency from './hooks/useFetchPreferredCurrency';
import useUpdatePreferredCurrency from './hooks/useUpdatePreferredCurrency';
import { trackSetPreferredCurrency, trackViewCurrenciesFromPanel } from '../../utils/usageTracker';
import CurrencySelectOption from './CurrencySelectOption';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MulticurrencyPanel = ({
  isDetail,
  onClose
}) => {
  const availableCurrencies = useSelector(getAvailableCurrencies);
  const isDealStageView = useSelector(getIsDealStageView);
  const updatePreferredCurrency = useUpdatePreferredCurrency();
  const {
    data: preferredCurrency,
    loading
  } = useFetchPreferredCurrency();
  const companyCurrency = useSelector(getCompanyCurrency);
  const [selectedCurrency, setSelectedCurrency] = useState(preferredCurrency || companyCurrency);
  useEffect(() => {
    setSelectedCurrency(preferredCurrency || companyCurrency);
  }, [companyCurrency, preferredCurrency]);
  const filterCurrencyOptions = ({
    value
  }) => availableCurrencies.includes(value);
  const savePreferredCurrency = () => {
    if (selectedCurrency) {
      updatePreferredCurrency(selectedCurrency).then(() => {
        trackSetPreferredCurrency({
          isDealStageView,
          isDetail,
          companyCurrency,
          currencyCode: selectedCurrency
        });
        onClose();
      }).catch(() => {
        // should be caught in updatePreferredCurrency
      });
    }
  };
  const handleSelectChange = useCallback(event => setSelectedCurrency(event.target.value), []);
  return /*#__PURE__*/_jsxs(UIPanel, {
    "data-test-id": "multicurrency-sidebar",
    width: 525,
    children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
      onClick: onClose
    }), /*#__PURE__*/_jsx(UIPanelHeader, {
      children: /*#__PURE__*/_jsx(Text, {
        as: "h2",
        color: "text-primary-on-fill-default",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "multicurrencyPanel.header"
        })
      })
    }), /*#__PURE__*/_jsx(UIPanelBody, {
      children: /*#__PURE__*/_jsxs(UIPanelSection, {
        children: [/*#__PURE__*/_jsx(Flex, {
          alignItems: "center",
          children: /*#__PURE__*/_jsxs(Text, {
            as: "h5",
            marginBlockEnd: "400",
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "multicurrencyPanel.preferredCurrency"
            }), /*#__PURE__*/_jsx(Badge, {
              marginInlineStart: "200",
              use: "new",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "multicurrencyPanel.newBadge"
              })
            })]
          })
        }), /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          children: [/*#__PURE__*/_jsxs(Flex, {
            flexDirection: "column",
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "multicurrencyPanel.chooseCurrencyMessage"
            }), /*#__PURE__*/_jsx(FormattedMessage, {
              message: "multicurrencyPanel.dealBoardMessage"
            })]
          }), /*#__PURE__*/_jsx(Box, {
            basis: 80,
            children: /*#__PURE__*/_jsx(HelpIcon, {
              title: /*#__PURE__*/_jsx(FormattedJSXMessage, {
                message: "multicurrencyPanel.tooltip_jsx",
                options: {
                  external: true,
                  href: Links.CURRENCY_SETTINGS,
                  onClick: () => trackViewCurrenciesFromPanel({
                    isDealStageView,
                    isDetail
                  })
                },
                elements: {
                  Link
                }
              }),
              tooltipPlacement: "bottom"
            })
          })]
        }), loading ? /*#__PURE__*/_jsx(LoadingSpinner, {
          marginBlockStart: "200"
        }) : /*#__PURE__*/_jsx(View, {
          marginBlockStart: "200",
          children: /*#__PURE__*/_jsx(CurrencySelect, {
            currenciesFilter: filterCurrencyOptions,
            currencyStyle: currencyStyles.long,
            "data-test-id": "multicurrency-select",
            dropdownFooter: /*#__PURE__*/_jsx(Link, {
              href: Links.CURRENCY_SETTINGS,
              target: "_blank",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "multicurrencyPanel.actions.editCurrencies"
              })
            }),
            value: selectedCurrency,
            optionComponent: CurrencySelectOption,
            onChange: handleSelectChange
          })
        })]
      })
    }), /*#__PURE__*/_jsxs(UIPanelFooter, {
      children: [/*#__PURE__*/_jsx(UIButton, {
        "data-test-id": "multicurrency-save-button",
        disabled: !selectedCurrency,
        onClick: savePreferredCurrency,
        use: "primary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "multicurrencyPanel.actions.save"
        })
      }), /*#__PURE__*/_jsx(UIButton, {
        onClick: onClose,
        use: "secondary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "multicurrencyPanel.actions.cancel"
        })
      })]
    })]
  });
};
export default MulticurrencyPanel;